import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { Image } from '../../types';
import { useFavorites } from '../../context/FavoritesContext';
import { useCart } from '../../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToastContext } from '../../context/ToastContext';
import { analytics } from '../../services/analytics';
import { api } from '../../services/api';

// Contenedor principal que oscurece toda la pantalla
const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.65); /* Fondo más transparente */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999999; /* Valor extremadamente alto para asegurar prioridad */
  animation: fadeIn 0.2s ease-out;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

// Contenedor de la imagen con información
const ImageContainer = styled.div`
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  max-width: 90vw;
  max-height: 90vh;
  width: auto;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease-out;
  transform-origin: center;
  position: relative;
  
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

// Contenedor de imagen con panel
const ImageContainer2 = styled.div<{ isVertical?: boolean; isHorizontal?: boolean }>`
  position: relative;
  width: ${props => props.isHorizontal ? 'auto' : '392px'};
  height: ${props => props.isHorizontal ? 'auto' : (props.isVertical ? '698px' : '392px')};
  padding: 20px;
  margin: 0 auto;
  flex-shrink: 0;

  /* Responsive reducido 19% total */
  @media (max-width: 1600px) {
    width: ${props => props.isHorizontal ? 'auto' : '352px'};
    height: ${props => props.isHorizontal ? 'auto' : (props.isVertical ? '625px' : '352px')};
  }

  @media (max-width: 1200px) {
    width: ${props => props.isHorizontal ? 'auto' : '308px'};
    height: ${props => props.isHorizontal ? 'auto' : (props.isVertical ? '547px' : '308px')};
  }

  @media (max-width: 900px) {
    width: ${props => props.isHorizontal ? 'auto' : '264px'};
    height: ${props => props.isHorizontal ? 'auto' : (props.isVertical ? '468px' : '264px')};
  }

  @media (max-width: 600px) {
    width: ${props => props.isHorizontal ? 'auto' : '247px'};
    height: ${props => props.isHorizontal ? 'auto' : (props.isVertical ? '437px' : '247px')};
  }
`;

// Imagen
const LargeImage = styled.img<{ isHorizontal?: boolean }>`
  width: ${props => props.isHorizontal ? 'auto' : '100%'};
  height: ${props => props.isHorizontal ? 'auto' : '100%'};
  display: block;
  ${props => props.isHorizontal ? `
    max-width: calc(90vw - 60px);
    max-height: 392px;
    object-fit: contain;

    @media (max-width: 1600px) { max-height: 352px; }
    @media (max-width: 1200px) { max-height: 308px; }
    @media (max-width: 900px) { max-height: 264px; }
    @media (max-width: 600px) { max-height: 247px; }
  ` : `
    object-fit: cover;
  `}
`;

// Contenedor de botones superior derecho
const ButtonContainer = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  gap: 8px;
  z-index: 10002;
`;

// Botón de información
const InfoButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.2s;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.7);
    transform: scale(1.1);
  }
`;

// Botón de cerrar
const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  font-size: 18px;
  font-weight: 600;
  transition: all 0.2s;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.7);
    transform: scale(1.1);
  }
`;

// Panel lateral deslizante (capa sobre la imagen)
const SlidingPanel = styled.div<{ show: boolean }>`
  position: absolute;
  top: 0;
  right: ${props => props.show ? '0' : '-100%'};
  width: 90%;
  height: 100%;
  background-color: #e8e9ea;
  border-left: 1px solid #eaeaea;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  transition: right 0.3s ease-in-out;
  z-index: 10001;
  overflow-y: auto;
  border-radius: 12px 0 0 12px;
  
  /* Auto-hide scrollbar en modo vertical de móvil */
  @media (max-width: 768px) and (orientation: portrait) {
    overflow-y: auto;
    
    /* Para webkit browsers (Safari, Chrome en móvil) - auto-hide */
    &::-webkit-scrollbar {
      width: 6px;
      background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.3);
      border-radius: 3px;
      opacity: 0;
      transition: opacity 0.3s ease;
    }
    
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    
    /* Mostrar scrollbar al hacer scroll */
    &:hover::-webkit-scrollbar-thumb,
    &:active::-webkit-scrollbar-thumb {
      opacity: 1;
    }
    
    /* Para Firefox - auto-hide */
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.3) transparent;
  }
`;

// Contenido del panel
const PanelContent = styled.div`
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

// Título del panel
const PanelTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #333;
  border-bottom: 2px solid #e3f2fd;
  padding-bottom: 8px;
`;

// Sección del panel
const PanelSection = styled.div`
  margin-bottom: 20px;
`;

// Subtítulo de sección
const SectionTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// Texto de descripción
const DescriptionText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #666;
  line-height: 1.5;
`;

// Contenedor de tags
const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

// Tag individual
const Tag = styled.span`
  background-color: #e3f2fd;
  color: #1565c0;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid #bbdefb;
`;

// Título de la imagen
const ImageTitle = styled.div`
  padding: 10px 20px;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  background-color: white;
  border-top: 1px solid #eaeaea;
  flex-shrink: 0;
`;

// Barra de acciones con iconos
const ActionBar = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px;
  background-color: #f8f8f8;
  border-top: 1px solid #eaeaea;
  flex-shrink: 0;
`;

const ActionButton = styled.button<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  margin: 0 15px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${props => props.isActive ? '#f0f0f0' : 'white'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  svg {
    width: 24px;
    height: 24px;
    fill: ${props => props.isActive ? '#FF5252' : '#757575'};
    transition: fill 0.2s ease;
  }
  
  &:nth-child(2) svg {
    fill: ${props => props.isActive ? '#1E88E5' : '#757575'};
  }
  
  &:nth-child(3) svg {
    fill: ${props => props.isActive ? '#FFC107' : '#757575'};
  }
  
  &:nth-child(4) svg {
    fill: #757575;
  }
`;

// Modal para opciones de compartir (desktop)
const ShareModal = styled.div<{ show: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${props => props.show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.2s ease-out;
`;

const ShareModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
`;

const ShareModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 1px solid #eee;
  padding-bottom: 16px;
`;

const ShareModalTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
`;

const ShareCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  
  &:hover {
    color: #333;
  }
`;

const ShareOptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const ShareOptionButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 12px;
  border: 1px solid #eee;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  color: inherit;
  
  &:hover {
    border-color: #ddd;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const ShareOptionIcon = styled.div`
  width: 40px;
  height: 40px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`;

const ShareOptionText = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #666;
  text-align: center;
`;

const ShareUrlContainer = styled.div`
  margin-top: 20px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
`;

const ShareUrlLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ShareUrlInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  font-family: monospace;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const CopyButton = styled.button`
  margin-top: 8px;
  padding: 6px 12px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: #0056b3;
  }
  
  &:active {
    background: #004085;
  }
`;

interface ImageViewerProps {
  image: Image;
  onClose: () => void;
  context?: 'browse' | 'favorites' | 'cart'; // Nuevo prop para identificar el módulo
}

const ImageViewer: React.FC<ImageViewerProps> = ({ image, onClose, context = 'browse' }) => {
  // Hooks para favoritos y carrito
  const { addToFavorites, removeFromFavorites, isFavorite } = useFavorites();
  const { addToCart, isInCart } = useCart();
  const { user } = useAuth();
  const { showWarning } = useToastContext();
  const navigate = useNavigate();
  
  // Estado del panel deslizante
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  
  // Estado del modal de compartir
  const [showShareModal, setShowShareModal] = useState(false);
  
  // Determinar si la imagen es vertical (igual que en ImageCard)
  const isVertical = image.height > image.width * 1.2;
  // Imagen horizontal (wide): se muestra completa, sin recortes
  const isHorizontal = image.width > image.height * 1.2;
  
  // Crear un portal para el modal
  const modalRoot = document.getElementById('modal-root') || document.body;
  
  // Track image view when component mounts
  useEffect(() => {
    analytics.viewImage(image.id);
  }, [image.id]);

  // Prevenir scroll y añadir evento de teclado
  useEffect(() => {
    // Guardar el estado anterior del overflow
    const originalStyle = window.getComputedStyle(document.body).overflow;
    // Prevenir scroll
    document.body.style.overflow = 'hidden';
    
    // Función para manejar tecla Escape
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    // Añadir evento para Escape
    document.addEventListener('keydown', handleEscape);
    
    // Limpiar al desmontar
    return () => {
      document.body.style.overflow = originalStyle;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Manejar clics en el fondo para cerrar
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  
  // Prevenir que los clics en la imagen se propaguen al fondo
  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };
  
  // Manejar favoritos
  const handleFavoriteClick = () => {
    if (!user) {
      showWarning('Haz login para agregar a favoritos');
      return;
    }
    
    if (isFavorite(image.id)) {
      removeFromFavorites(image.id);
    } else {
      addToFavorites(image);
    }
  };
  
  // Manejar añadir al carrito
  const handleCartClick = () => {
    if (!user) {
      showWarning('Haz login para agregar al carrito');
      return;
    }
    
    addToCart(image);
  };
  
  // Función para descargar archivo preview/marca de agua
  const downloadPreviewImage = async () => {
    try {
      const response = await fetch(image.url);
      if (!response.ok) {
        throw new Error(`Respuesta no exitosa del servidor: ${response.status}`);
      }
      const blob = await response.blob();
      if (!blob || blob.size === 0) {
        throw new Error('La imagen descargada está vacía');
      }
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `${image.title}_preview.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(url);
      showWarning('Imagen preview descargada');
    } catch (error) {
      showWarning('Error al descargar la imagen preview');
      console.error('Download error:', error);
    }
  };
  
  // Manejar la descarga - comportamiento diferente según el contexto
  const handleDownloadClick = () => {
    if (!user) {
      showWarning('Haz login para descargar imágenes');
      return;
    }
    
    if (context === 'cart') {
      // En el carrito: descargar archivo preview/marca de agua
      downloadPreviewImage();
    } else {
      // En browse/favoritos: comportamiento original (añadir al carrito y redirigir)
      addToCart(image);
      onClose();
      navigate('/cart');
    }
  };
  
  // Estados actuales
  const isImageInFavorites = isFavorite(image.id);
  const isImageInCart = isInCart(image.id);
  
  // Procesar tags (manejar tanto string como array)
  const tagsArray = image.tags 
    ? typeof image.tags === 'string' 
      ? image.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
      : Array.isArray(image.tags) 
        ? image.tags 
        : []
    : [];
  
  // Toggle del panel de información
  const handleInfoToggle = () => {
    setShowInfoPanel(!showInfoPanel);
  };
  
  // Crear URL completa para compartir
  const shareUrl = `https://canvasoft.com/catalog/previewcontent/?rsrc=${image.id}`;
  const shareText = `Mira esta increíble imagen de Canvasoft: ${image.title}`;
  
  // Función principal de compartir
  const handleShare = async () => {
    // Verificar si el navegador soporta Web Share API (móviles)
    if (navigator.share) {
      try {
        await navigator.share({
          title: image.title,
          text: shareText,
          url: shareUrl,
        });
      } catch (error) {
        // Si el usuario cancela o hay error, mostrar modal de fallback
        console.log('Share cancelled or failed:', error);
        setShowShareModal(true);
      }
    } else {
      // Fallback para desktop: mostrar modal con opciones
      setShowShareModal(true);
    }
  };
  
  // Funciones de compartir específicas para redes sociales
  const shareToFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };
  
  const shareToTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };
  
  const shareToWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
    window.open(url, '_blank');
  };
  
  const shareToTelegram = () => {
    const url = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank');
  };
  
  const shareToLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };
  
  const shareToEmail = () => {
    const subject = encodeURIComponent(image.title);
    const body = encodeURIComponent(`${shareText}\n\n${shareUrl}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };
  
  // Copiar URL al portapapeles
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      // Aquí podrías añadir una notificación de éxito
      alert('URL copiada al portapapeles');
    } catch (error) {
      // Fallback para navegadores que no soportan clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('URL copiada al portapapeles');
    }
  };

  // Manejar borrado de imagen (solo para usuarios admin)
  const handleDeleteImage = async () => {
    if (!user || (user.email !== 'javajmc@gmail.com' && user.email !== 'ilyana.vasquez@gmail.com')) {
      return;
    }

    try {
      await api.delete(`/canvamanager/imgs/${image.id}`, {
        headers: {
          'X-User-Email': user.email
        }
      });
      showWarning('Imagen borrada exitosamente');
      onClose(); // Cerrar el modal después de borrar
    } catch (error) {
      console.error('Error borrando imagen:', error);
      showWarning('Error al borrar la imagen');
    }
  };

  return ReactDOM.createPortal(
    <Modal onClick={handleBackdropClick}>
      <ImageContainer onClick={handleContentClick}>
        <ButtonContainer>
          <InfoButton onClick={handleInfoToggle} aria-label="Información">
            i
          </InfoButton>
          {/* Botón de borrar solo para usuarios admin */}
          {(user?.email === 'javajmc@gmail.com' || user?.email === 'ilyana.vasquez@gmail.com') && (
            <CloseButton onClick={handleDeleteImage} aria-label="Borrar imagen">
              🗑️
            </CloseButton>
          )}
          <CloseButton onClick={onClose} aria-label="Cerrar">
            ×
          </CloseButton>
        </ButtonContainer>
        <ImageContainer2 isVertical={isVertical} isHorizontal={isHorizontal}>
          <LargeImage src={image.url} alt={image.title} isHorizontal={isHorizontal} />
          
          <SlidingPanel show={showInfoPanel}>
            <PanelContent>
              <PanelSection>
                <SectionTitle>Descripción</SectionTitle>
                {image.description ? (
                  <DescriptionText>
                    {image.description.includes('--') 
                      ? image.description.substring(0, image.description.indexOf('--')).trim()
                      : image.description
                    }
                  </DescriptionText>
                ) : (
                  <DescriptionText style={{ fontStyle: 'italic', color: '#999' }}>
                    No hay descripción disponible
                  </DescriptionText>
                )}
              </PanelSection>
              
              <PanelSection>
                <SectionTitle>Tags</SectionTitle>
                {tagsArray.length > 0 ? (
                  <TagsContainer>
                    {tagsArray.map((tag, index) => (
                      <Tag key={index}>{tag}</Tag>
                    ))}
                  </TagsContainer>
                ) : (
                  <DescriptionText style={{ fontStyle: 'italic', color: '#999' }}>
                    No hay tags disponibles
                  </DescriptionText>
                )}
              </PanelSection>
            </PanelContent>
          </SlidingPanel>
        </ImageContainer2>
        
        <ImageTitle>{image.title}</ImageTitle>
        
        <ActionBar>
          {/* Botón de compartir */}
          <ActionButton 
            onClick={handleShare}
            aria-label="Compartir imagen"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.50-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z" />
            </svg>
          </ActionButton>
          
          {/* Botón de favoritos */}
          <ActionButton 
            onClick={handleFavoriteClick} 
            isActive={isImageInFavorites}
            aria-label={isImageInFavorites ? "Quitar de favoritos" : "Agregar a favoritos"}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
          </ActionButton>
          
          {/* Botón de carrito */}
          <ActionButton 
            onClick={handleCartClick} 
            isActive={isImageInCart}
            aria-label={isImageInCart ? "Ya está en el carrito" : "Agregar al carrito"}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" />
            </svg>
          </ActionButton>
          
          {/* Botón de descarga (lleva al carrito) */}
          <ActionButton 
            onClick={handleDownloadClick}
            aria-label="Descargar (ir al carrito)"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
            </svg>
          </ActionButton>
        </ActionBar>
      </ImageContainer>
      
      {/* Modal de compartir para desktop */}
      <ShareModal show={showShareModal} onClick={() => setShowShareModal(false)}>
        <ShareModalContent onClick={(e) => e.stopPropagation()}>
          <ShareModalHeader>
            <ShareModalTitle>Compartir imagen</ShareModalTitle>
            <ShareCloseButton onClick={() => setShowShareModal(false)}>×</ShareCloseButton>
          </ShareModalHeader>
          
          <ShareOptionsGrid>
            <ShareOptionButton onClick={shareToFacebook}>
              <ShareOptionIcon>📘</ShareOptionIcon>
              <ShareOptionText>Facebook</ShareOptionText>
            </ShareOptionButton>
            
            <ShareOptionButton onClick={shareToTwitter}>
              <ShareOptionIcon>🐦</ShareOptionIcon>
              <ShareOptionText>Twitter/X</ShareOptionText>
            </ShareOptionButton>
            
            <ShareOptionButton onClick={shareToWhatsApp}>
              <ShareOptionIcon>💬</ShareOptionIcon>
              <ShareOptionText>WhatsApp</ShareOptionText>
            </ShareOptionButton>
            
            <ShareOptionButton onClick={shareToTelegram}>
              <ShareOptionIcon>✈️</ShareOptionIcon>
              <ShareOptionText>Telegram</ShareOptionText>
            </ShareOptionButton>
            
            <ShareOptionButton onClick={shareToLinkedIn}>
              <ShareOptionIcon>💼</ShareOptionIcon>
              <ShareOptionText>LinkedIn</ShareOptionText>
            </ShareOptionButton>
            
            <ShareOptionButton onClick={shareToEmail}>
              <ShareOptionIcon>📧</ShareOptionIcon>
              <ShareOptionText>Email</ShareOptionText>
            </ShareOptionButton>
          </ShareOptionsGrid>
          
          <ShareUrlContainer>
            <ShareUrlLabel>URL para compartir</ShareUrlLabel>
            <ShareUrlInput 
              type="text" 
              value={shareUrl} 
              readOnly 
              onClick={(e) => (e.target as HTMLInputElement).select()}
            />
            <CopyButton onClick={copyToClipboard}>
              Copiar URL
            </CopyButton>
          </ShareUrlContainer>
        </ShareModalContent>
      </ShareModal>
    </Modal>,
    modalRoot
  );
};

export default ImageViewer;